import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldLabel } from '@/components/ui/field';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { signingError } from '@/features/emr/components/ReportSigningPanel';
export function SignatureCard() {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const { data, refetch } = useQuery<{ id: string; image_url: string } | null>({
    queryKey: ['my-signature'],
    queryFn: async () => (await clientApi.get('/api/v1/auth/signature')).data,
  });
  async function upload() {
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      await clientApi.post('/api/v1/auth/signature', body);
      await refetch();
      setFile(null);
      toast.success('Signature saved for future reports');
    } catch (e) {
      toast.error(signingError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Report signature</CardTitle>
        <CardDescription>
          Upload your own signature. Replacing it affects future signatures only. Your organization must separately
          authorize you to sign reports.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {data && (
          <img className="max-h-24 max-w-64 object-contain" src={data.image_url} alt="Your current report signature" />
        )}
        <Field>
          <FieldLabel htmlFor="signature-image">Signature image (PNG or JPEG, up to 2 MB)</FieldLabel>
          <Input
            id="signature-image"
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </Field>
      </CardContent>
      <CardFooter>
        <Button disabled={!file || busy} onClick={upload}>
          {busy ? 'Uploading…' : 'Upload my signature'}
        </Button>
      </CardFooter>
    </Card>
  );
}
